<template>
  <div class="library" :class="isThemeAja ? 'aja-bg-main' : ''">
    <Navigation />
    <v-main>
      <v-container v-if="$acl.not.check('isProPremium')">
        <v-row>
          <v-col cols="12">
            <NotPremiumCard/>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-else>
        <!-- Header -->
        <v-row>
          <v-col cols="12">
            <v-card elevation="4" :color="isThemeAja ? '#4E983C' : 'primary'" class="mb-3" :class="{'theme-aja-active': isThemeAja}">
              <v-card-text class="px-4 py-3">
                <v-row align="center" no-gutters>
                  <v-col cols="auto">
                    <v-icon x-large class="mr-4 white--text">library_books</v-icon>
                  </v-col>
                  <v-col>
                    <h1 class="headline mb-1 white--text">Bibliothèque de documents</h1>
                    <p class="subtitle-1 mb-0 white--text">
                      Accédez à tous les documents mis à votre disposition
                    </p>
                  </v-col>
                </v-row>
                <v-row align="center" >
                  <!-- Search -->
                  <v-col cols="12" md="6" class="pr-md-4">
                    <v-text-field
                      v-model="searchQuery"
                      prepend-inner-icon="search"
                      label="Rechercher un document..."
                      clearable
                      flat
                      solo
                      hide-details
                      :class="{'aja-input': isThemeAja}"
                      @input="debounceSearch"
                      @click:clear="clearSearch"
                    />
                  </v-col>
                  
                  <!-- Type Filter -->
                  <v-col cols="12" md="3" class="pr-md-2">
                    <v-select
                      v-model="selectedType"
                      :items="typeOptions"
                      label="Type de document"
                      flat
                      solo
                      clearable
                      hide-details
                      :class="{'aja-input': isThemeAja}"
                      @change="loadDocuments"
                    />
                  </v-col>
                  
                  <!-- Sort -->
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="sortBy"
                      :items="sortOptions"
                      label="Trier par"
                      flat
                      solo
                      hide-details
                      :class="{'aja-input': isThemeAja}"
                      @change="loadDocuments"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Documents Grid -->
        <v-row>
          <v-col cols="12">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular
                class="aja-btn-primary"
                indeterminate
                size="64"
              />
              <p class="mt-4 subtitle-1">Chargement des documents...</p>
            </div>
            <div v-else-if="documents.length === 0" elevation="2" class="text-center py-8">
              <v-icon size="80" class="aja-icon-primary mb-4">
                folder_open
              </v-icon>
              <h3 class="headline mb-2">Aucun document trouvé</h3>
              <p class="subtitle-1 opacity-75">
                {{ searchQuery || selectedType ? 'Aucun document ne correspond à vos critères.' : 'Aucun document disponible pour le moment.' }}
              </p>
              <v-btn
                v-if="searchQuery || selectedType"
                class="aja-btn-primary mt-4"
                @click="clearFilters"
              >
                Effacer les filtres
              </v-btn>
            </div>

            <div v-else>
              <v-row>
                <v-col
                  v-for="document in documents"
                  :key="document.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  xl="2"
                  class="document-col"
                >
                  <DocumentCard
                    :document="document"
                    :color="isThemeAja ? '#4E983C' : 'primary'"
                    @download="downloadDocument"
                    @preview="previewDocument"
                  />
                </v-col>
              </v-row>

              <!-- Pagination -->
              <v-row v-if="pagination.last_page > 1" class="mt-6">
                <v-col cols="12" class="text-center">
                  <v-pagination
                    v-model="currentPage"
                    :length="pagination.last_page"
                    :total-visible="7"
                    class="aja-btn-primary"
                    @input="changePage"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- PDF Preview Modal -->
      <DocumentViewer
        v-model="showPreview"
        :document="selectedDocument"
        :color="isThemeAja ? '#4E983C' : 'primary'"
      />
    </v-main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Navigation from '@/components/Navigation'
import DocumentCard from '@/components/documents/DocumentCard'
import DocumentViewer from '@/components/documents/DocumentViewer'
import NotPremiumCard from '@/components/NotPremiumCard'
import iSpringViewer from '@/mixins/iSpringViewer'

export default {
  name: 'ProLibrary',

  mixins: [iSpringViewer],

  components: {
    Navigation,
    DocumentCard,
    DocumentViewer,
    NotPremiumCard
  },

  data() {
    return {
      searchQuery: '',
      selectedType: null,
      sortBy: 'created_at',
      currentPage: 1,
      showPreview: false,
      selectedDocument: null,
      searchTimeout: null
    }
  },

  computed: {
    ...mapGetters([
      'getDocuments',
      'getDocumentTypes',
      'getDocumentsLoading',
      'getDocumentsPagination',
      'isThemeAja'
    ]),

    documents() {
      return this.getDocuments
    },

    loading() {
      return this.getDocumentsLoading
    },

    pagination() {
      return this.getDocumentsPagination
    },

    documentTypes() {
      return this.getDocumentTypes
    },

    typeOptions() {
      const options = [
        { text: 'Tous les types', value: null }
      ]
      
      Object.entries(this.documentTypes).forEach(([key, value]) => {
        options.push({
          text: value,
          value: key
        })
      })
      
      return options
    },

    sortOptions() {
      return [
        { text: 'Plus récent', value: 'created_at' },
        { text: 'Titre A-Z', value: 'title' },
        { text: 'Type', value: 'type' }
      ]
    }
  },

  watch: {
    selectedType() {
      this.currentPage = 1
      this.loadDocuments()
    },
    
    sortBy() {
      this.currentPage = 1
      this.loadDocuments()
    }
  },

  mounted() {
    this.initializeLibrary()
  },

  methods: {
    ...mapActions([
      'documentsRequest',
      'documentTypesRequest',
      'documentDownloadRequest'
    ]),

    async initializeLibrary() {
      try {
        await Promise.all([
          this.documentTypesRequest(),
          this.loadDocuments()
        ])
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error)
      }
    },

    async loadDocuments() {
      const queries = {
        page: this.currentPage,
        sort: this.sortBy,
        direction: this.sortBy === 'title' ? 'asc' : 'desc'
      }

      // Construire l'objet de recherche
      const searchData = {}
      
      if (this.searchQuery) {
        searchData.title = this.searchQuery
      }
      
      if (this.selectedType) {
        searchData.type = this.selectedType
      }
      
      // Ajouter la recherche seulement s'il y a des critères
      if (Object.keys(searchData).length > 0) {
        queries.search = JSON.stringify(searchData)
      }

      await this.documentsRequest(queries)
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadDocuments()
      }, 300)
    },

    clearSearch() {
      this.searchQuery = ''
      this.currentPage = 1
      this.loadDocuments()
    },

    clearFilters() {
      this.searchQuery = ''
      this.selectedType = null
      this.currentPage = 1
      this.loadDocuments()
    },

    async changePage(page) {
      this.currentPage = page
      await this.loadDocuments()
    },

    async downloadDocument(document) {
      try {
        await this.documentDownloadRequest(document.id)
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error)
      }
    },

    previewDocument(document) {
      // If iSpring format, open in new tab
      if (document.format === 'ispring') {
        this.openISpringDocument(document)
      } else {
        // PDF format - open in modal viewer
        this.selectedDocument = document
        this.showPreview = true
      }
    }
  }
}
</script>

<style scoped>
.library {
  min-height: 100vh;
}

.opacity-75 {
  opacity: 0.75;
}

.document-col {
  margin-bottom: 16px;
}

/* Réduire la hauteur des cartes */
.document-col :deep(.document-card) {
  height: auto;
  min-height: 200px;
}

.document-col :deep(.document-card__header) {
  padding: 12px 16px 0;
}

.document-col :deep(.document-card__icon-wrapper) {
  height: 50px;
  margin-bottom: 8px;
}

.document-col :deep(.document-card__content) {
  padding: 0 16px 12px;
}

.document-col :deep(.document-card__title) {
  font-size: 0.95rem;
  margin-bottom: 6px;
  -webkit-line-clamp: 2;
}

.document-col :deep(.document-card__description) {
  font-size: 0.8rem;
  margin-bottom: 8px;
  -webkit-line-clamp: 2;
}

.document-col :deep(.document-card__meta) {
  gap: 2px;
}

.document-col :deep(.document-card__meta-item) {
  font-size: 0.75rem;
}

.document-col :deep(.document-card__actions) {
  padding: 8px 16px 12px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .document-col :deep(.document-card) {
    min-height: 180px;
  }
  
  .document-col :deep(.document-card__header) {
    padding: 10px 12px 0;
  }
  
  .document-col :deep(.document-card__content) {
    padding: 0 12px 10px;
  }
  
  .document-col :deep(.document-card__actions) {
    padding: 6px 12px 10px;
  }
}
</style> 